import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar, useAuth } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { useToast } from "../hooks/use-toast";
import { fetchInviteVendor, authLogin, authSignup, authMe, api } from "../lib/api";

export default function JoinPage() {
  const { invite_token } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setToken, setUser, user, token } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, vendor: null });
  const [showSignup, setShowSignup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Save invite token to localStorage for later use
  useEffect(() => {
    if (invite_token) {
      localStorage.setItem('prepmate_last_invite_token', invite_token);
    }
  }, [invite_token]);

  useEffect(() => {
    (async () => {
      try {
        const v = await fetchInviteVendor(invite_token);
        setState({ loading: false, error: null, vendor: v });
      } catch (e) {
        setState({ loading: false, error: (e?.response?.data?.detail || 'Invalid invite link'), vendor: null });
      }
    })();
  }, [invite_token]);

  // If user is already logged in, bind to vendor and redirect
  useEffect(() => {
    if (user && state.vendor && !state.loading) {
      if (user.vendor_id === state.vendor.vendor_id) {
        toast({ title: `Welcome back!`, description: `You're already joined with ${state.vendor.vendor_name}` });
        navigate(`/company/${state.vendor.vendor_id}`);
      } else if (!user.vendor_id) {
        bindUserToVendor(token, state.vendor.vendor_id, state.vendor.vendor_name);
      } else {
        toast({
          title: `Switching vendor`,
          description: `Joining ${state.vendor.vendor_name}…`,
        });
        bindUserToVendor(token, state.vendor.vendor_id, state.vendor.vendor_name);
      }
    }
  }, [user, state.vendor, state.loading]);

  async function bindUserToVendor(accessToken, vendorId, vendorName) {
    try {
      await api.post('/users/me/vendor', { vendor_id: vendorId }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      const updated = await authMe();
      setUser(updated);
      toast({ title: `Joined ${vendorName}!`, description: 'You can now browse the menu and place orders.' });
      navigate(`/company/${vendorId}`);
    } catch (e) {
      toast({ title: 'Could not join vendor', description: e?.response?.data?.detail || 'Please try again.' });
    }
  }

  async function handleSignup(e) {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast({ title: 'Missing fields', description: 'Please fill in all fields.' });
      return;
    }
    setIsSubmitting(true);
    try {
      const { access_token } = await authSignup({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: 'customer',
      });
      setToken(access_token);
      localStorage.setItem('prepmate_token', access_token);
      await bindUserToVendor(access_token, state.vendor.vendor_id, state.vendor.vendor_name);
    } catch (e) {
      toast({ title: 'Sign up failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast({ title: 'Missing fields', description: 'Please fill in email and password.' });
      return;
    }
    setIsSubmitting(true);
    try {
      const { access_token } = await authLogin({ email: formData.email, password: formData.password });
      setToken(access_token);
      localStorage.setItem('prepmate_token', access_token);
      const u = await authMe();
      setUser(u);
      await bindUserToVendor(access_token, state.vendor.vendor_id, state.vendor.vendor_name);
    } catch (e) {
      toast({ title: 'Sign in failed', description: e?.response?.data?.detail || 'Invalid credentials.' });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (state.loading) return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="animate-pulse rounded-xl border bg-white p-8 space-y-4">
          <div className="h-6 bg-neutral-200 rounded w-2/3 mx-auto" />
          <div className="h-4 bg-neutral-200 rounded w-full" />
          <div className="h-4 bg-neutral-200 rounded w-3/4" />
          <div className="h-10 bg-neutral-200 rounded mt-4" />
        </div>
      </div>
    </div>
  );

  if (state.error) return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-md px-4 py-10">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="text-lg font-semibold mb-2">Invalid invite link</div>
            <div className="text-sm text-neutral-500 mb-6">This invite link is not valid or has expired.</div>
            <Button onClick={() => navigate('/auth')} className="bg-[#2a7d4f] text-white hover:bg-[#236840]">
              Go to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const v = state.vendor;

  return (
    <div>
      <Navbar />

      {/* Vendor hero */}
      {v.image && (
        <div className="w-full h-48 md:h-64 overflow-hidden">
          <img src={v.image} alt={v.vendor_name} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="mx-auto max-w-md px-4 py-8">
        <h1 className="text-2xl font-bold mb-1">{v.vendor_name}</h1>
        <p className="text-sm text-neutral-500 mb-6">Create an account or sign in to start ordering.</p>

        {/* Signup form */}
        {showSignup && (
          <form onSubmit={handleSignup} className="space-y-3 mb-4">
            <Input
              placeholder="Your name"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="bg-white"
              data-testid="signup-name-input"
            />
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              className="bg-white"
              data-testid="signup-email-input"
            />
            <Input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={e => setFormData({ ...formData, password: e.target.value })}
              className="bg-white"
              data-testid="signup-password-input"
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting} className="bg-[#2a7d4f] text-white hover:bg-[#236840] flex-1" data-testid="signup-submit-btn">
                {isSubmitting ? 'Creating account…' : 'Create account'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowSignup(false); setFormData({ name: '', email: '', password: '' }); }} data-testid="signup-cancel-btn">
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Login form */}
        {showLogin && (
          <form onSubmit={handleLogin} className="space-y-3 mb-4">
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              className="bg-white"
              data-testid="login-email-input"
            />
            <Input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={e => setFormData({ ...formData, password: e.target.value })}
              className="bg-white"
              data-testid="login-password-input"
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting} className="bg-[#2a7d4f] text-white hover:bg-[#236840] flex-1" data-testid="login-submit-btn">
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowLogin(false); setFormData({ name: '', email: '', password: '' }); }} data-testid="login-cancel-btn">
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Initial CTA buttons */}
        {!showSignup && !showLogin && (
          <div className="flex flex-col gap-3">
            <Button onClick={() => setShowSignup(true)} className="h-12 bg-[#2a7d4f] text-white hover:bg-[#236840] text-base" data-testid="show-signup-btn">
              Create account
            </Button>
            <Button variant="outline" className="h-12 bg-white text-base" onClick={() => setShowLogin(true)} data-testid="show-login-btn">
              Sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
